import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { apiService, apiAdmin } from '../../services/apiService';
import { SearchParameterType } from '../../types';
import { useToast } from '../../contexts/ToastContext';

interface CurlImportProps {
  onImport: (apiData: any) => void;
  onCancel: () => void;
}

const EXAMPLE_CURL = `curl -X GET "https://leakcheck.io/api/v2/query/test@example.com" \\
  -H "Accept: application/json" \\
  -H "X-API-Key: YOUR_API_KEY"`;

export const CurlImport: React.FC<CurlImportProps> = ({ onImport, onCancel }) => {
  const { showToast } = useToast();
  const [content, setContent] = useState('');
  const [parameterTypeId, setParameterTypeId] = useState<number | ''>('');
  const [parameterTypes, setParameterTypes] = useState<SearchParameterType[]>([]);
  const [previewData, setPreviewData] = useState<any>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    apiService.getParameterTypes().then(setParameterTypes).catch(console.error);
  }, []);

  const handlePreview = async () => {
    if (!content.trim()) {
      showToast('Paste a cURL command first', 'error');
      return;
    }
    if (!parameterTypeId) {
      showToast('Select a parameter type', 'error');
      return;
    }
    setIsPreviewing(true);
    try {
      const result = await apiAdmin.previewCurl(content, Number(parameterTypeId));
      setPreviewData(result);
      if (result.success) {
        showToast('cURL parsed successfully', 'success');
      } else {
        showToast(result.message || 'Parse failed', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Preview failed', 'error');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!content.trim() || !parameterTypeId) {
      showToast('cURL content and parameter type are required', 'error');
      return;
    }
    setIsImporting(true);
    try {
      const result = await apiAdmin.importCurl(content, Number(parameterTypeId));
      if (result.success) {
        showToast(result.message || 'API imported', 'success');
        onImport(result.api);
      } else {
        showToast(result.message || 'Import failed', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Import failed', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  const handleUsePreview = () => {
    if (!previewData?.api_data) return;
    const data = previewData.api_data;
    onImport({
      ...data,
      searchParameterTypeId: parameterTypeId,
      organizationId: previewData.organization?.id,
      status: 'active',
    });
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">Import from cURL</p>
        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
          Paste any cURL from API docs (RapidAPI, Postman, Swagger). Headers, method, URL, and body are auto-detected.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          Parameter Type *
        </label>
        <select
          value={parameterTypeId}
          onChange={(e) => setParameterTypeId(e.target.value ? Number(e.target.value) : '')}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
        >
          <option value="">Select parameter type</option>
          {parameterTypes.map((pt) => (
            <option key={pt.id} value={pt.id}>{pt.display_name}</option>
          ))}
        </select>
      </div>

      <div>
        <div className="flex justify-between items-center mb-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200">cURL Command *</label>
          <button
            type="button"
            onClick={() => setContent(EXAMPLE_CURL)}
            className="text-xs text-indigo-600 hover:text-indigo-800"
          >
            Load example
          </button>
        </div>
        <textarea
          value={content}
          onChange={(e) => { setContent(e.target.value); setPreviewData(null); }}
          rows={10}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-xs bg-white dark:bg-gray-800"
          placeholder="curl -X GET 'https://api.example.com/...' -H 'Authorization: Bearer ...'"
        />
      </div>

      {previewData?.api_data && (
        <Card className="p-4">
          <h4 className="text-sm font-semibold mb-2">Parsed Configuration</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-gray-500">Name:</span> {previewData.api_data.name}</div>
            <div><span className="text-gray-500">Method:</span> <Badge>{previewData.api_data.requestMethod}</Badge></div>
            <div className="col-span-2"><span className="text-gray-500">URL:</span> {previewData.api_data.baseUrl}</div>
            <div><span className="text-gray-500">Format:</span> {previewData.api_data.parameterFormat}</div>
            <div><span className="text-gray-500">Param:</span> {previewData.api_data.parameterName || '—'}</div>
          </div>
          {Object.keys(previewData.api_data.headers || {}).length > 0 && (
            <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-x-auto">
              {JSON.stringify(previewData.api_data.headers, null, 2)}
            </pre>
          )}
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" onClick={handlePreview} disabled={isPreviewing}>
          {isPreviewing ? 'Parsing...' : 'Preview'}
        </Button>
        <Button type="button" onClick={handleImport} disabled={isImporting}>
          {isImporting ? 'Importing...' : 'Import & Save'}
        </Button>
        {previewData?.api_data && (
          <Button type="button" variant="secondary" onClick={handleUsePreview}>
            Edit Before Save
          </Button>
        )}
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};
